import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FilterSchema } from '@adc/blueprint'
import DataTableUtils from '../utils'
import type {
  FilterAppModel,
  SavedSearchFilterClause,
} from '../utils/savedSearchFilters'

/**
 * Redux-free saved search state + actions for the new DataTable + FilterToolbar
 * pattern, persisting in the LEGACY saved-search wire format so migrated grids
 * stay compatible with the DataTableWrapper flow:
 *
 *   parameters = {
 *     columns: JSON.stringify(<storage-format columns>),
 *     filters: JSON.stringify(<FilterClause[]>),
 *   }
 *
 * Serialization reuses this package's own prepSavedSearchDataForPost/Put; the
 * blueprint filterState <-> FilterClause conversion is handled by the
 * savedSearchFilters converters. The API layer is injected via `providers` so
 * the package stays transport-agnostic (apps supply their own graph/REST calls).
 */

export type SavedSearchType = 'PERSONAL' | 'GLOBAL'

export interface SavedSearchProviders {
  getSavedSearches: (
    req: unknown,
    namespace: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any[]>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postSavedSearch: (req: unknown, values: any) => Promise<unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  putSavedSearch: (req: unknown, values: any) => Promise<unknown>
  deleteSavedSearch: (req: unknown, id: string) => Promise<unknown>
}

export interface ParsedSavedSearch {
  saved_search_id: string
  saved_search_name: string
  saved_search_type: SavedSearchType
  namespace: string
  parameters: {
    columns: unknown[]
    filters: SavedSearchFilterClause[]
  }
}

export interface SavedSearchError {
  status?: number
  statusText: string
}

export interface SavedSearchesState {
  searches: ReadonlyArray<ParsedSavedSearch>
  selectedSearchId: string | null
  isDirty: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dirtyColumns: any[] | null
  errorInfo: SavedSearchError | null
}

export interface SavedSearchesActions {
  setSelectedSavedSearch: (id: string | null) => void
  setSavedSearchDirty: (dirty: boolean) => void
  setSavedSearchDirtyColumns: (columns: unknown[]) => void
  fetchSavedSearches: () => Promise<void>
  postSavedSearch: (name: string, type: SavedSearchType) => Promise<void>
  putSavedSearch: () => Promise<void>
  deleteSavedSearch: (id: string) => Promise<void>
}

export interface UseSavedSearchesArgs {
  /** Backend namespace / table type, e.g. 'ORGANIZATION'. */
  namespace: string
  /** Blueprint filter definitions (from useFilters data.filterDefinitions). */
  filterDefinitions: FilterSchema.FilterGroupDefinitionArray | undefined
  /** Injected transport for saved-search CRUD. */
  providers: SavedSearchProviders
  /** Read the grid's current blueprint filter state. */
  getFilterState: () => FilterAppModel
  /** Apply a blueprint filter state back into the grid. */
  applyFilterState: (filterState: FilterAppModel) => void
  /** Max personal saved searches before create is blocked. Default 100. */
  maxPersonalSavedSearches?: number
  paramVersion?: number
}

export interface UseSavedSearchesResult {
  savedSearches: SavedSearchesState
  savedSearchesActions: SavedSearchesActions
}

const LIMIT_MESSAGE =
  'You have reached the maximum number of saved searches. Please delete some before saving more.'

/**
 * Defensive parse of a saved search's stringified `parameters`. Absent params
 * are treated as empty (a search may legitimately store only columns or only
 * filters); malformed params (bad JSON or unexpected shape) fall back to [] but
 * emit a warning so a silently-empty result is traceable rather than mysterious.
 */
const parseSearch = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  search: any
): ParsedSavedSearch => {
  const searchId = search?.saved_search_id ?? '(unknown)'
  const safeParse = (raw: unknown, field: 'columns' | 'filters'): unknown[] => {
    // Absent is normal (not every search persists both) -> empty, no warning.
    if (raw === null || raw === undefined) return []
    if (Array.isArray(raw)) return raw
    if (typeof raw !== 'string') {
      console.warn(
        `[DataTable] useSavedSearches: saved search ${searchId} has non-string parameters.${field}; ignoring.`,
        raw
      )
      return []
    }
    try {
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) {
        console.warn(
          `[DataTable] useSavedSearches: saved search ${searchId} parameters.${field} parsed to a non-array; ignoring.`,
          parsed
        )
        return []
      }
      return parsed
    } catch (error) {
      console.warn(
        `[DataTable] useSavedSearches: saved search ${searchId} has malformed parameters.${field} JSON; ignoring.`,
        error
      )
      return []
    }
  }
  return {
    ...search,
    parameters: {
      columns: safeParse(search?.parameters?.columns, 'columns'),
      filters: safeParse(
        search?.parameters?.filters,
        'filters'
      ) as SavedSearchFilterClause[],
    },
  }
}

export function useSavedSearches({
  namespace,
  filterDefinitions,
  providers,
  getFilterState,
  applyFilterState,
  maxPersonalSavedSearches = 100,
  paramVersion = 1,
}: UseSavedSearchesArgs): UseSavedSearchesResult {
  const [searches, setSearches] = useState<ReadonlyArray<ParsedSavedSearch>>([])
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dirtyColumns, setDirtyColumns] = useState<any[] | null>(null)
  const [errorInfo, setErrorInfo] = useState<SavedSearchError | null>(null)

  const getFilterStateRef = useRef(getFilterState)
  const applyFilterStateRef = useRef(applyFilterState)
  const filterDefinitionsRef = useRef(filterDefinitions)
  const providersRef = useRef(providers)
  const searchesRef = useRef(searches)
  const dirtyColumnsRef = useRef(dirtyColumns)
  const selectedIdRef = useRef(selectedSearchId)
  getFilterStateRef.current = getFilterState
  applyFilterStateRef.current = applyFilterState
  filterDefinitionsRef.current = filterDefinitions
  providersRef.current = providers
  searchesRef.current = searches
  dirtyColumnsRef.current = dirtyColumns
  selectedIdRef.current = selectedSearchId

  const buildTableInfo = useCallback(
    () => ({
      filterClauses: DataTableUtils.filterStateToFilterClauses(
        getFilterStateRef.current(),
        filterDefinitionsRef.current
      ),
    }),
    []
  )

  const fetchSavedSearches = useCallback(async (): Promise<void> => {
    try {
      const content = await providersRef.current.getSavedSearches(
        undefined,
        namespace
      )
      setSearches((content ?? []).map(parseSearch))
    } catch (error) {
      console.error('[DataTable] useSavedSearches: fetch failed', error)
      setSearches([])
      setErrorInfo({ statusText: 'Unable to load saved searches.' })
    }
  }, [namespace])

  useEffect(() => {
    fetchSavedSearches().catch(() => {})
  }, [fetchSavedSearches])

  const setSelectedSavedSearch = useCallback((id: string | null): void => {
    setSelectedSearchId(id)
    setIsDirty(false)
    setDirtyColumns(null)
    if (id) {
      const match = searchesRef.current.find(s => s.saved_search_id === id)
      if (match) {
        applyFilterStateRef.current(
          DataTableUtils.filterClausesToFilterState(
            match.parameters.filters,
            filterDefinitionsRef.current
          )
        )
      }
    }
  }, [])

  const setSavedSearchDirty = useCallback((dirty: boolean): void => {
    setIsDirty(dirty)
  }, [])

  const setSavedSearchDirtyColumns = useCallback((columns: unknown[]): void => {
    setDirtyColumns(columns as unknown[])
  }, [])

  const postSavedSearch = useCallback(
    async (name: string, type: SavedSearchType): Promise<void> => {
      setErrorInfo(null)
      const personalCount = searchesRef.current.filter(
        s => s.saved_search_type === 'PERSONAL'
      ).length
      if (type !== 'GLOBAL' && personalCount >= maxPersonalSavedSearches) {
        setErrorInfo({ status: 400, statusText: LIMIT_MESSAGE })
        return
      }
      try {
        const payload = DataTableUtils.prepSavedSearchDataForPost(
          buildTableInfo(),
          {
            dirtyColumns: dirtyColumnsRef.current,
            searches: searchesRef.current,
          },
          name,
          namespace,
          type
        )
        await providersRef.current.postSavedSearch(undefined, {
          ...payload,
          param_version: paramVersion,
        })
        setIsDirty(false)
        await fetchSavedSearches()
      } catch (error) {
        console.error('[DataTable] useSavedSearches: post failed', error)
        setErrorInfo({ statusText: 'Unable to save search.' })
      }
    },
    [
      buildTableInfo,
      fetchSavedSearches,
      maxPersonalSavedSearches,
      namespace,
      paramVersion,
    ]
  )

  const putSavedSearch = useCallback(async (): Promise<void> => {
    setErrorInfo(null)
    if (!selectedIdRef.current) return
    try {
      const payload = DataTableUtils.prepSavedSearchDataForPut(
        buildTableInfo(),
        {
          dirtyColumns: dirtyColumnsRef.current,
          searches: searchesRef.current,
          selectedSearchId: selectedIdRef.current,
        }
      )
      await providersRef.current.putSavedSearch(undefined, {
        ...payload,
        namespace,
        param_version: paramVersion,
      })
      setIsDirty(false)
      await fetchSavedSearches()
    } catch (error) {
      console.error('[DataTable] useSavedSearches: put failed', error)
      setErrorInfo({ statusText: 'Unable to update saved search.' })
    }
  }, [buildTableInfo, fetchSavedSearches, namespace, paramVersion])

  const deleteSavedSearch = useCallback(
    async (id: string): Promise<void> => {
      setErrorInfo(null)
      try {
        await providersRef.current.deleteSavedSearch(undefined, id)
        setSelectedSearchId(prev => (prev === id ? null : prev))
        await fetchSavedSearches()
      } catch (error) {
        console.error('[DataTable] useSavedSearches: delete failed', error)
        setErrorInfo({ statusText: 'Unable to delete saved search.' })
      }
    },
    [fetchSavedSearches]
  )

  const savedSearchesActions = useMemo<SavedSearchesActions>(
    () => ({
      setSelectedSavedSearch,
      setSavedSearchDirty,
      setSavedSearchDirtyColumns,
      fetchSavedSearches,
      postSavedSearch,
      putSavedSearch,
      deleteSavedSearch,
    }),
    [
      setSelectedSavedSearch,
      setSavedSearchDirty,
      setSavedSearchDirtyColumns,
      fetchSavedSearches,
      postSavedSearch,
      putSavedSearch,
      deleteSavedSearch,
    ]
  )

  const savedSearches = useMemo<SavedSearchesState>(
    () => ({ searches, selectedSearchId, isDirty, dirtyColumns, errorInfo }),
    [searches, selectedSearchId, isDirty, dirtyColumns, errorInfo]
  )

  return { savedSearches, savedSearchesActions }
}
